import { Label } from '@headlessui/react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FOOTER_HEIGHT, TRADE_PAGE_SCROLLABLE_TABLE_ROWS } from '../../../constants';
import { ASSET_ROUTE_URL } from '../../../constants/routes';
import { useFlattenedOrders, useOpenOrders } from '../../../hooks/useOrders';
import { usePositions } from '../../../hooks/usePositions';
import { ProductType } from '../../../hooks/useProducts';
import { useActiveSelectedSubaccount } from '../../../hooks/useSelectedSubaccount';
import { cn } from '../../../lib/classNameHelpers';
import { Card } from '../../Card';
import { Button } from '../../catalyst-ui-kit/button';
import { Checkbox, CheckboxField } from '../../catalyst-ui-kit/checkbox';
import { InlineSelect } from '../../InlineSelect/InlineSelect';
import { OpenOrdersTable } from '../../OpenOrdersTable/OpenOrdersTable';
import { OrderHistoryTable } from '../../OrderHistoryTable/OrderHistoryTable';
import { PnlTable } from '../../PnlTable/PnlTable';
import { PositionsTable } from '../../PositionsTable/PositionsTable';
import { ScrollContainer } from '../../ScrollContainer/ScrollContainer';
import { TradeHistoryTable } from '../../TradeHistoryTable/TradeHistoryTable';

const OPTIONS = [
  { value: 'positions', label: 'Positions' },
  { value: 'openOrders', label: 'Open Orders' },
  { value: 'pnl', label: 'P&L' },
  { value: 'orderHistory', label: 'Order History' },
  { value: 'tradeHistory', label: 'Trade History' },
] as const;

type OptionValues = (typeof OPTIONS)[number]['value'];

const TABLE_MAX_HEIGHT = 512;

export const TradeFooter: React.FC<{ product: ProductType }> = ({ product }) => {
  const navigate = useNavigate({ from: ASSET_ROUTE_URL });
  const { tableTab: tab, showAllMarkets } = useSearch({ from: ASSET_ROUTE_URL });

  const selectedSubaccount = useActiveSelectedSubaccount();
  const productIdFilter = showAllMarkets ? undefined : product.id;
  const { data: orderData, hasNextPage } = useOpenOrders({
    subaccountId: selectedSubaccount?.id,
    limit: TRADE_PAGE_SCROLLABLE_TABLE_ROWS,
    productIds: productIdFilter ? [productIdFilter] : undefined,
  });
  const numOfOrders = useFlattenedOrders(orderData?.pages).length;
  const { data: positions } = usePositions({
    subaccountId: selectedSubaccount?.id,
    productIds: productIdFilter ? [productIdFilter] : undefined,
  });

  const optionBadges: Record<OptionValues, string | null> = useMemo(
    () => ({
      pnl: null,
      orderHistory: null,
      tradeHistory: null,
      openOrders: numOfOrders ? ` (${numOfOrders}${hasNextPage ? '+' : ''})` : null,
      positions: positions?.length ? ` (${positions.length})` : null,
    }),
    [numOfOrders, hasNextPage, positions?.length]
  );

  const scrollableContainerRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number>(TABLE_MAX_HEIGHT);

  // Set up resize listener to call updateMaxHeight
  useEffect(() => {
    // If the available space from the top of the scrollable container to the
    // top of the footer is greater than TABLE_MAX_HEIGHT, then allow the
    // scrollable container to grow.
    const updateMaxHeight = () => {
      if (!scrollableContainerRef.current) return;

      const scrollableRect = scrollableContainerRef.current.getBoundingClientRect();
      const borderHeights = 2;
      const availableSpace =
        window.innerHeight - scrollableRect.top - FOOTER_HEIGHT - borderHeights;
      if (availableSpace > TABLE_MAX_HEIGHT) {
        setMaxHeight(availableSpace);
      } else {
        setMaxHeight(TABLE_MAX_HEIGHT);
      }
    };

    updateMaxHeight();

    window.addEventListener('resize', updateMaxHeight);
    return () => window.removeEventListener('resize', updateMaxHeight);
  }, []);

  return (
    <Card className="size-full">
      <ScrollContainer className="flex items-center justify-between overflow-x-auto border-solid border-borderPrimary lg:border-b">
        <InlineSelect
          selected={tab}
          onChange={(option) => {
            if (option) {
              void navigate({ search: (prev) => ({ ...prev, tableTab: option.value }) });
            }
          }}
          options={OPTIONS}
          renderOption={({ option, isSelected, defaultProps }) => {
            return (
              <Button
                text
                size="xs"
                color="secondary"
                className={cn('!py-4 !px-2 whitespace-nowrap', isSelected && 'text-contentPrimary')}
                {...defaultProps}
              >
                {option.label}
                {optionBadges[option.value]}
              </Button>
            );
          }}
        />
        <CheckboxField className="mx-2 !gap-x-2 whitespace-nowrap text-xs lg:mx-3">
          <Checkbox
            checked={showAllMarkets}
            onChange={(x) => {
              void navigate({ search: (prev) => ({ ...prev, showAllMarkets: x }) });
            }}
            color="tertiary"
          />
          <Label>All Markets</Label>
        </CheckboxField>
      </ScrollContainer>
      <div ref={scrollableContainerRef} className="p-px">
        {tab === 'positions' && (
          <PositionsTable
            products={!showAllMarkets ? [product] : undefined}
            defaultPageSize={TRADE_PAGE_SCROLLABLE_TABLE_ROWS}
            className="overflow-y-auto"
            style={{ maxHeight }}
          />
        )}
        {tab === 'openOrders' && (
          <OpenOrdersTable
            products={!showAllMarkets ? [product] : undefined}
            defaultPageSize={TRADE_PAGE_SCROLLABLE_TABLE_ROWS}
            className="overflow-y-auto"
            style={{ maxHeight }}
          />
        )}
        {tab === 'orderHistory' && (
          <OrderHistoryTable
            pagination={false}
            products={!showAllMarkets ? [product] : undefined}
          />
        )}
        {tab === 'tradeHistory' && (
          <TradeHistoryTable
            pagination={false}
            products={!showAllMarkets ? [product] : undefined}
          />
        )}
        {tab === 'pnl' && (
          <PnlTable pagination={false} products={!showAllMarkets ? [product] : undefined} />
        )}
      </div>
    </Card>
  );
};
